/**
 * Disables variant option controls when no matching variant exists.
 * Sold-out variants remain selectable; only missing combinations are disabled.
 */
(function () {
  const DISABLED_CLASS = 'variant-option--unavailable';

  function isPrimaryPicker(picker) {
    return !picker.parentElement?.closest('variant-selects, variant-radios');
  }

  function isElementHidden(element) {
    if (element.closest('.material-hidden-slc, [hidden], .hidden')) return true;

    const style = window.getComputedStyle(element);
    return style.display === 'none' || style.visibility === 'hidden';
  }

  function getOptionGroups(picker) {
    const groups = [];

    if (picker.tagName === 'VARIANT-SELECTS') {
      Array.from(picker.children).forEach((child) => {
        if (child.tagName === 'VARIANT-RADIOS') {
          child.querySelectorAll(':scope > fieldset').forEach((fieldset) => {
            groups.push({ type: 'radio', element: fieldset });
          });
          return;
        }

        if (child.classList.contains('material-hidden-slc')) return;

        const select = child.querySelector('select');
        if (select) {
          groups.push({ type: 'select', element: select });
        }
      });

      return groups;
    }

    if (picker.tagName === 'VARIANT-RADIOS') {
      picker.querySelectorAll(':scope > fieldset').forEach((fieldset) => {
        groups.push({ type: 'radio', element: fieldset });
      });
    }

    return groups;
  }

  function isGroupVisible(group) {
    return !isElementHidden(group.element);
  }

  function getVariantData(picker) {
    const script =
      picker.querySelector(':scope > script[type="application/json"]') ||
      picker.querySelector('script[type="application/json"]');

    if (!script) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return [];
    }
  }

  function getVariantOptions(variant) {
    if (Array.isArray(variant.options) && variant.options.length) return variant.options;
    return [variant.option1, variant.option2, variant.option3].filter(Boolean);
  }

  function getSelectedOptions(groups) {
    return groups.map((group) => {
      if (group.type === 'radio') {
        const checked = group.element.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : '';
      }

      return group.element.value || '';
    });
  }

  function variantExists(variants, optionIndex, optionValue, selectedOptions) {
    return variants.some((variant) => {
      const options = getVariantOptions(variant);

      return options.every((value, index) => {
        if (index === optionIndex) return value === optionValue;

        const selected = selectedOptions[index];
        if (!selected) return true;

        return value === selected;
      });
    });
  }

  function setRadioAvailability(input, isAvailable) {
    input.disabled = !isAvailable;
    input.classList.toggle(DISABLED_CLASS, !isAvailable);

    const label = input.nextElementSibling;
    if (label?.tagName === 'LABEL') {
      label.classList.toggle(DISABLED_CLASS, !isAvailable);
    }
  }

  function setSelectOptionAvailability(option, isAvailable) {
    option.disabled = !isAvailable;
    option.classList.toggle(DISABLED_CLASS, !isAvailable);
  }

  function updateGroupAvailability(group, optionIndex, variants, selectedOptions) {
    if (group.type === 'radio') {
      group.element.querySelectorAll('input[type="radio"]').forEach((input) => {
        const exists = variantExists(variants, optionIndex, input.value, selectedOptions);
        setRadioAvailability(input, exists);
      });
      return;
    }

    Array.from(group.element.options).forEach((option) => {
      if (!option.value) return;
      const exists = variantExists(variants, optionIndex, option.value, selectedOptions);
      setSelectOptionAvailability(option, exists);
    });
  }

  function correctInvalidSelection(group) {
    if (group.type === 'radio') {
      const checked = group.element.querySelector('input[type="radio"]:checked');
      if (!checked || !checked.disabled) return false;

      const firstEnabled = group.element.querySelector('input[type="radio"]:not(:disabled)');
      if (firstEnabled) {
        firstEnabled.checked = true;
        return true;
      }

      checked.checked = false;
      return true;
    }

    const select = group.element;
    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption?.value || !selectedOption.disabled) return false;

    const firstEnabled = Array.from(select.options).find((option) => option.value && !option.disabled);
    select.value = firstEnabled ? firstEnabled.value : '';
    return true;
  }

  let isUpdating = false;

  function updatePicker(picker) {
    if (!picker || !isPrimaryPicker(picker)) return;

    const variants = getVariantData(picker);
    const groups = getOptionGroups(picker);
    if (!variants.length || !groups.length) return;

    const selectedOptions = getSelectedOptions(groups);

    groups.forEach((group, optionIndex) => {
      if (!isGroupVisible(group)) return;
      updateGroupAvailability(group, optionIndex, variants, selectedOptions);
    });

    const corrected = groups.filter(isGroupVisible).some((group) => correctInvalidSelection(group));
    if (corrected) {
      const nextSelectedOptions = getSelectedOptions(groups);
      groups.filter(isGroupVisible).forEach((group, optionIndex) => {
        const groupIndex = groups.indexOf(group);
        updateGroupAvailability(group, groupIndex, variants, nextSelectedOptions);
      });
      picker.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function refreshAllPickers() {
    document.querySelectorAll('variant-radios, variant-selects').forEach((picker) => {
      updatePicker(picker);
    });
  }

  function handleChange(event) {
    if (isUpdating) return;

    const target = event.target;
    if (
      target.tagName !== 'SELECT' &&
      !(target.matches?.('input[type="radio"]') && target.closest('variant-radios, variant-selects'))
    ) {
      return;
    }

    const picker = target.closest('variant-radios, variant-selects');
    if (!picker) return;

    const primaryPicker = isPrimaryPicker(picker)
      ? picker
      : picker.closest('variant-selects, variant-radios');

    if (!primaryPicker || !isPrimaryPicker(primaryPicker)) return;

    isUpdating = true;
    try {
      updatePicker(primaryPicker);
    } finally {
      isUpdating = false;
    }
  }

  function init() {
    if (window.VariantPicker?._initialized) {
      refreshAllPickers();
      return;
    }

    document.addEventListener('change', handleChange, true);
    refreshAllPickers();

    window.VariantPicker = {
      _initialized: true,
      update: updatePicker,
      refreshAll: refreshAllPickers,
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
